/**
 * 둥근 모서리 사각형 렌더링 유틸리티
 * 성능 최적화: 조건부 분기 최소화, Path2D 재사용
 */

/**
 * 둥근 사각형 경로를 CanvasRenderingContext2D에 그리기
 * @param ctx Canvas 컨텍스트
 * @param x X 좌표
 * @param y Y 좌표
 * @param width 너비
 * @param height 높이
 * @param topLeft 좌상단 반경
 * @param topRight 우상단 반경
 * @param bottomLeft 좌하단 반경
 * @param bottomRight 우하단 반경
 */
export function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  topLeft: number = 0,
  topRight: number = 0,
  bottomLeft: number = 0,
  bottomRight: number = 0
): void {
  ctx.beginPath();
  
  // 최대 반경 제한 (너비와 높이의 절반)
  const maxRadius = Math.min(width, height) / 2;
  const tl = Math.min(topLeft, maxRadius);
  const tr = Math.min(topRight, maxRadius);
  const bl = Math.min(bottomLeft, maxRadius);
  const br = Math.min(bottomRight, maxRadius);
  
  // 좌상단부터 시계방향으로 경로 생성
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + width - tr, y);
  if (tr > 0) {
    ctx.arcTo(x + width, y, x + width, y + tr, tr);
  }
  ctx.lineTo(x + width, y + height - br);
  if (br > 0) {
    ctx.arcTo(x + width, y + height, x + width - br, y + height, br);
  }
  ctx.lineTo(x + bl, y + height);
  if (bl > 0) {
    ctx.arcTo(x, y + height, x, y + height - bl, bl);
  }
  ctx.lineTo(x, y + tl);
  if (tl > 0) {
    ctx.arcTo(x, y, x + tl, y, tl);
  }
  
  ctx.closePath();
}

/**
 * 둥근 사각형 경로를 Path2D로 생성 (테두리 렌더링용, 재사용 가능)
 * @param x X 좌표
 * @param y Y 좌표
 * @param width 너비
 * @param height 높이
 * @param topLeft 좌상단 반경
 * @param topRight 우상단 반경
 * @param bottomLeft 좌하단 반경
 * @param bottomRight 우하단 반경
 * @returns Path2D 객체
 */
export function createRoundedRectPath(
  x: number,
  y: number,
  width: number,
  height: number,
  topLeft: number = 0,
  topRight: number = 0,
  bottomLeft: number = 0,
  bottomRight: number = 0
): Path2D {
  const p = new Path2D();
  
  // 최대 반경 제한 (너비와 높이의 절반)
  const maxRadius = Math.min(width, height) / 2;
  const tl = Math.min(topLeft, maxRadius);
  const tr = Math.min(topRight, maxRadius);
  const bl = Math.min(bottomLeft, maxRadius);
  const br = Math.min(bottomRight, maxRadius);
  
  // 좌상단부터 시계방향으로 경로 생성
  p.moveTo(x + tl, y);
  p.lineTo(x + width - tr, y);
  if (tr > 0) {
    p.arcTo(x + width, y, x + width, y + tr, tr);
  }
  p.lineTo(x + width, y + height - br);
  if (br > 0) {
    p.arcTo(x + width, y + height, x + width - br, y + height, br);
  }
  p.lineTo(x + bl, y + height);
  if (bl > 0) {
    p.arcTo(x, y + height, x, y + height - bl, bl);
  }
  p.lineTo(x, y + tl);
  if (tl > 0) {
    p.arcTo(x, y, x + tl, y, tl);
  }
  
  return p;
}

/**
 * 테두리 위치에 따라 모서리 반경 보정
 * @param r 원본 반경
 * @param borderWidth 테두리 두께
 * @param position 테두리 위치
 * @param origWidth 원본 너비 (비례 축소 계산용)
 * @param origHeight 원본 높이 (비례 축소 계산용)
 * @param actualWidth 실제 테두리 영역 너비 (alignForStroke 보정 후, inside일 때 정확한 계산용)
 * @param actualHeight 실제 테두리 영역 높이 (alignForStroke 보정 후, inside일 때 정확한 계산용)
 * @returns 보정된 반경
 */
export function adjustCornerRadiusForBorder(
  r: number,
  borderWidth: number,
  position: 'outside' | 'center' | 'inside' = 'center',
  origWidth?: number,
  origHeight?: number,
  actualWidth?: number,
  actualHeight?: number
): number {
  if (!borderWidth) return r || 0;
  if (position === 'outside') return (r || 0) + borderWidth; // 외부: 반경 + 두께
  if (position === 'inside') {
    // 내부: strokeRect는 중심선 기준이므로, 원본 객체의 외부 경계 반경과 테두리의 외부 경계 반경이 같아야 함
    // 테두리 중심선의 반경 = 원본 반경 - borderWidth/2
    // 이렇게 하면 원본 반경과 테두리 반경이 같을 때 정확히 일치함
    return Math.max(0, (r || 0) - borderWidth / 2);
  }
  return r || 0; // 중앙: 동일
}

